import React, { useEffect, useRef, useState } from "react";

const TreeSelect = ({
  options = [],
  value,
  onChange,
  isClearable = true,
  placeholder = "Select...",
  error = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownRef = useRef(null);

  // ✅ close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (node) => {
    onChange({ label: node.label, value: node.value });
    setIsOpen(false);
    setSearchText("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  // ✅ filter nodes based on search text
  const filterNodes = (nodes, text) => {
    if (!text) return nodes;
    return nodes.reduce((acc, node) => {
      const matchedChildren = node.children
        ? filterNodes(node.children, text)
        : [];
      if (
        node.label.toLowerCase().includes(text.toLowerCase()) ||
        matchedChildren.length > 0
      ) {
        acc.push({ ...node, children: matchedChildren });
      }
      return acc;
    }, []);
  };

  const filteredOptions = filterNodes(options, searchText);

  return (
    <div className="tree-select" ref={dropdownRef}>
      {/* ✅ Selected value display */}
      <div
        className={`tree-select__control ${
          isOpen ? "tree-select__control--focused" : ""
        } ${error ? "tree-select__control--error" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="tree-select__value-container">
          {value ? (
            <span className="tree-select__value">{value.label}</span>
          ) : (
            <span className="tree-select__placeholder">{placeholder}</span>
          )}
        </div>

        <div className="tree-select__indicators">
          {isClearable && value && (
            <button
              type="button"
              className="tree-select__clear"
              onClick={handleClear}
              aria-label="Clear selection"
            >
              <svg width="14" height="14" viewBox="0 0 20 20">
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
              </svg>
            </button>
          )}
          <span className="tree-select__separator" />
          <span
            className={`tree-select__arrow ${
              isOpen ? "tree-select__arrow--open" : ""
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 20 20">
              <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
            </svg>
          </span>
        </div>
      </div>

      {/* ✅ Dropdown */}
      {isOpen && (
        <div className="tree-select__dropdown">
          {/* ✅ Search */}
          <div className="tree-select__search">
            <svg
              className="tree-select__search-icon"
              width="16"
              height="16"
              viewBox="0 0 20 20"
            >
              <path d="M14.386 14.386l4.0877 4.0877-4.0877-4.0877c-2.9418 2.9419-7.7115 2.9419-10.6533 0-2.9419-2.9418-2.9419-7.7115 0-10.6533 2.9418-2.9419 7.7115-2.9419 10.6533 0 2.9419 2.9418 2.9419 7.7115 0 10.6533z" />
            </svg>
            <input
              type="text"
              placeholder="Search options..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            {searchText && (
              <button
                type="button"
                className="tree-select__search-clear"
                onClick={() => setSearchText("")}
              >
                ×
              </button>
            )}
          </div>

          {/* ✅ Tree nodes */}
          <div className="tree-select__menu">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((node) => (
                <TreeNode
                  key={node.value}
                  node={node}
                  selectedValue={value?.value}
                  onSelect={handleSelect}
                  searchText={searchText}
                />
              ))
            ) : (
              <div className="tree-select__no-options">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                    fill="#cbd5e0"
                  />
                </svg>
                <p>No options found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ✅ Recursive TreeNode component
const TreeNode = ({ node, selectedValue, onSelect, depth = 0, searchText }) => {
  const [isExpanded, setIsExpanded] = useState(!!searchText);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedValue === node.value;

  useEffect(() => {
    if (searchText) {
      setIsExpanded(true);
    }
  }, [searchText]);

  const highlightText = (text, search) => {
    if (!search) return text;
    const parts = text.split(new RegExp(`(${search})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <mark key={index} className="tree-node__highlight">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  return (
    <div className="tree-node">
      <div
        className={`tree-node__item ${
          isSelected ? "tree-node__item--selected" : ""
        }`}
        style={{ paddingLeft: `${depth * 20 + 12}px` }}
      >
        {/* ✅ expand/collapse icon */}
        {hasChildren ? (
          <button
            type="button"
            className="tree-node__toggle"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 20 20"
              className={isExpanded ? "tree-node__toggle--expanded" : ""}
            >
              <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
            </svg>
          </button>
        ) : (
          <span className="tree-node__toggle tree-node__toggle--empty" />
        )}

        {/* ✅ node label */}
        <span className="tree-node__label" onClick={() => onSelect(node)}>
          {highlightText(node.label, searchText)}
        </span>

        {/* ✅ selected checkmark */}
        {isSelected && (
          <span className="tree-node__checkmark">
            <svg width="14" height="14" viewBox="0 0 20 20">
              <path d="M7.629 14.566c-0.125 0.125-0.291 0.188-0.456 0.188s-0.331-0.062-0.456-0.188l-4.406-4.406c-0.252-0.252-0.252-0.659 0-0.911l0.911-0.911c0.252-0.252 0.659-0.252 0.911 0l3.039 3.039 7.039-7.039c0.252-0.252 0.659-0.252 0.911 0l0.911 0.911c0.252 0.252 0.252 0.659 0 0.911l-8.404 8.406z" />
            </svg>
          </span>
        )}
      </div>

      {/* ✅ render children recursively */}
      {hasChildren && isExpanded && (
        <div className="tree-node__children">
          {node.children.map((child) => (
            <TreeNode
              key={child.value}
              node={child}
              selectedValue={selectedValue}
              onSelect={onSelect}
              depth={depth + 1}
              searchText={searchText}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TreeSelect;
